import {
  FormHelperText,
  Modal,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { FieldArray, Form, Formik } from "formik";
import { getAPICall, postAPICall } from "lib/callApi";
import { GET_ALL_PERMISSION, CREATE_PERMISSION_PROFILE, PERMISSIONS_NAME } from "lib/constants";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { enqueueSnackbar } from "notistack";
import Loader from "utils/Loader";
import * as Yup from "yup";
import { checkPermission } from "utils/HelperFunctions";
import { checkUserModuleComponentEnabledById } from "utils/Modules";

const validationSchema = Yup.object({
  profile_name: Yup.string()
    .max(50, "Profile name cannot be more than 50 characters")
    .required("Profile name is required"),
});

function AddPermissionsProfile(props) {
  const { open, setOpen, fetchRoles } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [permissionFetchLoading, setPermissionFetchLoading] = useState(false);
  const user = useSelector((state) => state.loginUser.loginUserState);
  const modules = useSelector((state) => state.modules.enabledModules);
  const [initialValues, setInitialValues] = useState({
    profile_name: "",
    permissions: [],
  });
  const [httpError, setHttpError] = useState({ error: false, message: "" });

  const [controller] = useMaterialUIController();

  const handleClose = () => {
    if (!isLoading) {
      setInitialValues({
        profile_name: "",
        permissions: [],
      });
      setHttpError({ error: false, message: "" });
      setOpen(false);
    }
  };

  const isSwitchChecked = (permission, values) => {
    try {
      if (
        values.find((x) => x.permission === PERMISSIONS_NAME.ADMIN).allow &&
        permission.permission !== PERMISSIONS_NAME.INPUT_KPIS
      ) {
        return true;
      }
      return permission.allow;
    } catch (err) {
      return false;
    }
  };

  const isSwitchDisabled = (permission, values) => {
    try {
      if (permission.disabled && permission.moduleCheck) {
        return true;
      }

      if (
        values.find((x) => x.permission === PERMISSIONS_NAME.ADMIN).allow &&
        permission.permission !== PERMISSIONS_NAME.INPUT_KPIS &&
        permission.permission !== PERMISSIONS_NAME.ADMIN
      ) {
        return true;
      }
      return permission.disabled;
    } catch (err) {
      return false;
    }
  };

  const initialDisabledState = (permission) => {
    try {
      if (
        permission.permission === PERMISSIONS_NAME.ADMIN &&
        !checkPermission(PERMISSIONS_NAME.ADMIN, user.userPermission)
      ) {
        return { disabled: true };
      }

      if (permission.FK_modules_id && !permission.FK_modules_components_id) {
        return {
          disabled: !checkUserModuleComponentEnabledById(modules, permission.FK_modules_id),
          moduleCheck: true,
        };
      }

      if (permission.FK_modules_id && permission.FK_modules_components_id) {
        return {
          disabled: !checkUserModuleComponentEnabledById(
            modules,
            permission.FK_modules_id,
            permission.FK_modules_components_id
          ),
          moduleCheck: true,
        };
      }

      return { disabled: false };
    } catch {
      return { disabled: false };
    }
  };

  const fetchAllPermission = async () => {
    try {
      setPermissionFetchLoading(true);
      const data = await getAPICall(GET_ALL_PERMISSION, true);

      if (data?.data?.success) {
        setInitialValues((prev) => ({
          ...prev,
          permissions: data?.data?.data?.permissionsList?.map((permission) => ({
            ...permission,
            allow: false,
            ...initialDisabledState(permission),
          })),
        }));
      } else {
        handleClose();
        if (data.response.status !== 403) {
          enqueueSnackbar(
            JSON.stringify({
              icon: "error",
              title: "Error",
              text: data?.response?.data?.message || "Something Went Wrong",
            }),
            {
              variant: "custom",
            }
          );
        }
      }
      setPermissionFetchLoading(false);
    } catch (error) {
      setPermissionFetchLoading(false);
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data.message || error.message,
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  useEffect(() => {
    if (open) {
      fetchAllPermission();
    }
    return () => {
      setInitialValues({
        profile_name: "",
        permissions: [],
      });
    };
  }, [open]);

  const { darkMode } = controller;

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: window.innerWidth < 600 ? "100%" : 700,
    bgcolor: darkMode ? "#1F283E" : "background.paper",
    overflow: "auto",
    boxShadow: 24,
    p: 4,
    outline: "none",
    maxHeight: "90%",
    borderRadius: "0.25em",
  };

  const handleSubmit = async (data) => {
    try {
      const dataToSubmit = {
        profileName: data.profile_name,
        profilePermissions: data?.permissions
          ?.filter((permission) => permission.allow)
          ?.map((permission) => ({ permissionId: permission.id })),
      };
      setIsLoading(true);

      const response = await postAPICall(CREATE_PERMISSION_PROFILE, true, dataToSubmit);

      if (response?.data?.success) {
        handleClose();
        fetchRoles();
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: response?.data?.message,
          }),
          {
            variant: "custom",
          }
        );
      } else {
        if (response?.response?.status === 403) {
          handleClose();
        }
        setHttpError({
          error: true,
          message: response?.response?.data?.message || "Something went wrong.",
        });
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data.message || error.message,
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <MDBox sx={style}>
        <MDBox sx={{ position: "relative" }}>
          <MDTypography variant="h4" mb={2}>
            New Permissions Profile
          </MDTypography>

          <MDBox sx={{ position: "relative" }}>
            <Formik
              enableReinitialize
              validateOnChange
              validationSchema={validationSchema}
              initialValues={initialValues}
              onSubmit={handleSubmit}
            >
              {({ values, setFieldValue, touched, errors }) => (
                <Form>
                  <Stack
                    spacing={2}
                    sx={{
                      "& .MuiFormHelperText-root": { color: "#d32f2f" },
                    }}
                  >
                    <MDInput
                      name="profile_name"
                      label="Profile Name"
                      onChange={(event) => {
                        setFieldValue("profile_name", event.target.value);
                      }}
                      helperText={touched.profile_name && errors.profile_name}
                      error={touched.profile_name && Boolean(errors.profile_name)}
                      sx={{ width: "60%" }}
                    />

                    <FieldArray
                      name="permissions"
                      render={() => (
                        <MDBox sx={{ position: "relative" }}>
                          <Loader loading={permissionFetchLoading} />

                          <TableContainer
                            component={Paper}
                            sx={{
                              maxHeight:
                                window.innerHeight < 800
                                  ? 0.5 * window.innerHeight
                                  : 0.6 * window.innerHeight,
                              userSelect: "none",
                              boxShadow: "none",
                              borderRadius: 0,
                            }}
                          >
                            <Table aria-label="simple table">
                              <TableHead sx={{ display: "table-header-group" }}>
                                <TableRow>
                                  <TableCell width="250px">Permission</TableCell>
                                  <TableCell align="center">Description</TableCell>
                                  <TableCell align="center" />
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {values?.permissions?.map((permission, index) => (
                                  <TableRow hover>
                                    <TableCell>{permission.displayName}</TableCell>
                                    <TableCell>
                                      {permission.disabled && permission.moduleCheck
                                        ? "Not available on current Alysio plan!"
                                        : permission.description}
                                    </TableCell>
                                    <TableCell sx={{ padding: "0.4rem 1rem" }} align="center">
                                      <Switch
                                        checked={isSwitchChecked(permission, values.permissions)}
                                        disabled={isSwitchDisabled(permission, values.permissions)}
                                        onChange={(event) => {
                                          setFieldValue(
                                            `permissions[${index}].allow`,
                                            event.target.checked
                                          );
                                        }}
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          {httpError.error && (
                            <MDBox
                              sx={{
                                "& .MuiFormHelperText-root": {
                                  color: "#d32f2f",
                                  fontSize: "16px",
                                  mt: 2,
                                  pl: 1,
                                },
                              }}
                            >
                              {" "}
                              <FormHelperText>{httpError.message}</FormHelperText>
                            </MDBox>
                          )}
                        </MDBox>
                      )}
                    />
                    <MDBox sx={{ display: "flex", justifyContent: "end", mt: 1 }}>
                      <MDButton
                        color="gray80"
                        onClick={handleClose}
                        sx={{ mt: 1, mr: 1, position: "relative" }}
                      >
                        Cancel
                      </MDButton>
                      <MDButton
                        color="primaryRed"
                        type="submit"
                        disabled={isLoading || permissionFetchLoading}
                        sx={{ mt: 1, position: "relative" }}
                      >
                        {isLoading ? "Loading..." : "Save"}
                      </MDButton>
                    </MDBox>
                  </Stack>
                </Form>
              )}
            </Formik>
          </MDBox>
        </MDBox>
      </MDBox>
    </Modal>
  );
}

export default AddPermissionsProfile;
